import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { refreshAdminNotifications } from "@/components/NotificationBell";
import { AdminEmptyState, AdminPageHeader, AdminPagination, AdminSubTabs, paginateItems } from "@/pages/admin/AdminUx";

const RECORD_DELETABLE = new Set(["APPROVED", "REJECTED", "CANCELLED"]);

function quoteAmount(q) {
  return q?.total_amount ?? Math.round((q?.supply_amount ?? q?.total ?? 0) * 1.1);
}

function fmtDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString("ko-KR");
}

export default function DeleteRequestsTab() {
  const { user } = useAuth();
  const isOwner = user?.role === "super_admin";
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get("request");
  const [rows, setRows] = useState([]);
  const [archivedQuotes, setArchivedQuotes] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [section, setSection] = useState("requests");
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [hardDeleting, setHardDeleting] = useState(false);

  const load = async () => {
    try {
      const [req, quotes] = await Promise.all([
        api.get("/admin/delete-requests"),
        api.get("/admin/quotes", { params: { archived: true } }).catch(() => ({ data: [] })),
      ]);
      setRows(req.data || []);
      setArchivedQuotes(quotes.data || []);
    } catch {
      toast.error("삭제 요청을 불러오지 못했습니다.");
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!focusId) return;
    const el = document.querySelector(`[data-testid="delete-request-${focusId}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusId, rows]);

  const decide = async (row, ok) => {
    if (ok) {
      const msg = `정말 이 데이터를 삭제/보관 처리하시겠습니까?\n대상: ${row.resource_title || "-"}\n고객: ${row.customer_name || "-"}\n요청자: ${row.requested_by_name || "-"}`;
      if (!window.confirm(msg)) return;
    }
    try {
      await api.post(`/admin/delete-requests/${row.id}/${ok ? "approve" : "reject"}`, { confirm: true });
      toast.success(ok ? "승인 후 보관 처리했습니다." : "거절했습니다. 원본은 유지됩니다.");
      load();
      refreshAdminNotifications();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "처리 실패");
    }
  };

  const handleHardDeleteQuote = async () => {
    if (!quoteToDelete || hardDeleting) return;
    setHardDeleting(true);
    try {
      await api.delete(`/admin/quotes/${quoteToDelete.id}/hard`);
      toast.success("견적이 삭제되었습니다.");
      const deletedId = quoteToDelete.id;
      setQuoteToDelete(null);
      setArchivedQuotes((prev) => prev.filter((row) => row.id !== deletedId));
      refreshAdminNotifications();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "삭제 실패");
    } finally {
      setHardDeleting(false);
    }
  };

  const handleHardDeleteRecord = async () => {
    if (!recordToDelete || hardDeleting) return;
    setHardDeleting(true);
    try {
      await api.delete(`/admin/delete-requests/${recordToDelete.id}/hard`);
      toast.success("삭제 요청 기록이 제거되었습니다.");
      const deletedId = recordToDelete.id;
      setRecordToDelete(null);
      setRows((prev) => prev.filter((row) => row.id !== deletedId));
      refreshAdminNotifications();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "기록 삭제 실패");
    } finally {
      setHardDeleting(false);
    }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (rows || []).filter((row) => {
      if (status !== "all" && row.status !== status) return false;
      if (!needle) return true;
      return [row.requested_by_name, row.resource_title, row.resource_type, row.customer_name, row.status, row.reason].filter(Boolean).join(" ").toLowerCase().includes(needle);
    });
  }, [rows, q, status]);
  const paged = paginateItems(filtered, page, 20);
  const archivedPaged = paginateItems(archivedQuotes, page, 20);

  return (
    <div className="space-y-4" data-testid="admin-delete-requests">
      <AdminPageHeader
        title="삭제 요청"
        description="영업 담당자의 삭제 요청을 승인하거나 거절합니다. 보관된 견적은 OWNER만 확인합니다."
      />
      <AdminSubTabs
        tabs={[{ id: "requests", label: "삭제 요청" }, { id: "archived", label: "보관된 견적" }]}
        value={section}
        onChange={(id) => { setSection(id); setPage(1); }}
      />
      {section === "requests" && (
        <>
          <div className="admin-toolbar">
            <input className="admin-toolbar__search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="요청자 / 대상 / 상태 검색" />
            <select className="admin-filter" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
              <option value="all">전체 상태</option>
              <option value="PENDING">대기</option>
              <option value="APPROVED">승인</option>
              <option value="REJECTED">거절</option>
            </select>
            <span className="admin-count">{filtered.length}건</span>
          </div>
          {filtered.length === 0 ? (
            <AdminEmptyState message="대기 중인 요청이 없습니다." />
          ) : (
            <div className="admin-row-list">
              {paged.items.map((row) => (
                <div
                  key={row.id}
                  data-testid={`delete-request-${row.id}`}
                  className={`admin-row ${focusId === row.id ? "admin-quote-card--focus" : ""}`}
                >
                  <div className="admin-row__main">
                    <div className="admin-row__title">{row.resource_title || row.resource_id}</div>
                    <div className="admin-row__meta">
                      {row.resource_type} · 고객 {row.customer_name || "-"} · 요청자 {row.requested_by_name} ({row.requested_by_sales_level}) · {row.status} · {fmtDate(row.created_at || row.requested_at)}
                    </div>
                    <div className="admin-row__preview">사유: {row.reason}{row.reason_detail ? ` / ${row.reason_detail}` : ""}</div>
                  </div>
                  <div className="admin-row__actions">
                    {row.status === "PENDING" && (
                      <>
                        <button type="button" className="admin-row__btn" onClick={() => decide(row, true)} data-testid={`delete-approve-${row.id}`}>승인</button>
                        <button type="button" className="admin-row__btn" onClick={() => decide(row, false)}>거절</button>
                      </>
                    )}
                    {isOwner && RECORD_DELETABLE.has(row.status) && (
                      <button
                        type="button"
                        className="admin-row__btn admin-row__btn--danger"
                        onClick={() => setRecordToDelete(row)}
                        data-testid={`delete-request-record-delete-${row.id}`}
                      >
                        기록 삭제
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <AdminPagination page={paged.page} pageSize={20} total={filtered.length} onPageChange={setPage} />
        </>
      )}
      {section === "archived" && (
        <>
          {archivedQuotes.length === 0 ? (
            <AdminEmptyState message="보관된 견적이 없습니다." />
          ) : (
            <div className="admin-row-list">
              {archivedPaged.items.map((quoteRow) => (
                <div key={quoteRow.id} className="admin-row" data-testid={`archived-quote-${quoteRow.id}`}>
                  <div className="admin-row__main">
                    <div className="admin-row__title">{quoteRow.title}</div>
                    <div className="admin-row__meta">
                      {quoteRow.customer_name || quoteRow.customer_email || "-"} · {quoteAmount(quoteRow).toLocaleString()}원 · {quoteRow.status || "보관"} · 보관 {fmtDate(quoteRow.archived_at)}
                    </div>
                  </div>
                  {isOwner && (
                    <div className="admin-row__actions">
                      <button
                        type="button"
                        className="admin-row__btn admin-row__btn--danger"
                        onClick={() => setQuoteToDelete(quoteRow)}
                        data-testid={`quote-hard-delete-${quoteRow.id}`}
                      >
                        삭제
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <AdminPagination page={archivedPaged.page} pageSize={20} total={archivedQuotes.length} onPageChange={setPage} />
        </>
      )}
      {quoteToDelete && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="owner-hard-delete-modal">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 w-full max-w-md space-y-3">
            <h4 className="font-medium">이 견적을 정말 삭제하시겠습니까?</h4>
            <p className="text-sm text-white/70">견적명: {quoteToDelete.title || "-"}</p>
            <p className="text-sm text-white/70">고객명: {quoteToDelete.customer_name || quoteToDelete.customer_email || "-"}</p>
            <p className="text-sm text-white/70">총 금액: {quoteAmount(quoteToDelete).toLocaleString()}원</p>
            <p className="text-sm text-white/70">상태: {quoteToDelete.status || "-"}</p>
            <p className="text-xs text-red-300">삭제한 견적은 복구할 수 없습니다.</p>
            <div className="flex gap-2">
              <button type="button" disabled={hardDeleting} onClick={() => setQuoteToDelete(null)} className="border border-white/10 rounded-lg px-3 py-2 text-sm">취소</button>
              <button type="button" disabled={hardDeleting} onClick={handleHardDeleteQuote} className="bg-red-500 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50" data-testid="owner-hard-delete-submit">
                {hardDeleting ? "삭제 중..." : "삭제 실행"}
              </button>
            </div>
          </div>
        </div>
      )}
      {recordToDelete && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="delete-request-record-delete-modal">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 w-full max-w-md space-y-3">
            <h4 className="font-medium">이 삭제 요청 기록을 삭제하시겠습니까?</h4>
            <p className="text-sm text-white/70">요청 대상: {recordToDelete.resource_title || recordToDelete.resource_id || "-"}</p>
            <p className="text-sm text-white/70">요청자: {recordToDelete.requested_by_name || "-"}</p>
            <p className="text-sm text-white/70">요청 사유: {recordToDelete.reason}{recordToDelete.reason_detail ? ` / ${recordToDelete.reason_detail}` : ""}</p>
            <p className="text-sm text-white/70">상태: {recordToDelete.status || "-"}</p>
            <p className="text-sm text-white/70">요청일: {fmtDate(recordToDelete.created_at || recordToDelete.requested_at)}</p>
            <p className="text-xs text-white/50">요청 기록만 삭제되며 원본 견적·계약·상담 데이터에는 영향을 주지 않습니다.</p>
            <div className="flex gap-2">
              <button type="button" disabled={hardDeleting} onClick={() => setRecordToDelete(null)} className="border border-white/10 rounded-lg px-3 py-2 text-sm">취소</button>
              <button type="button" disabled={hardDeleting} onClick={handleHardDeleteRecord} className="bg-red-500 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50" data-testid="delete-request-record-delete-submit">
                {hardDeleting ? "삭제 중..." : "기록 삭제"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
